import { Select } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import React, { useCallback, useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import { TableData, TableRow } from "../../../../components/Table/types";
import {
  LimitRequestType,
  LimitTopProductCategory,
  RolePermission,
  UpdateLimitRequestInlineInput,
} from "../../../../graphql/generated.ts";
import { useCurrentUserContext } from "../../../../providers/CurrentUserProvider.tsx";
import {
  LimitRequest,
  toHumanReadableLimitRequestStatus,
  topProductCategoryOptions,
} from "../../../../types/limitRequest/limitRequest.ts";
import { toLocalDate } from "../../../../utils/date";
import { toCurrencyPrice, toLocalNumber } from "../../../../utils/number";
import { toHumanReadable } from "../../../../utils/string";
import { getUserDisplayName, hasPermission } from "../../../../utils/user.ts";

interface Props {
  data: LimitRequest[] | null | undefined;
  onEdit: (id: number, value: UpdateLimitRequestInlineInput) => void;
}

export const useOngoingCUTableData = ({ data, onEdit }: Props): TableData => {
  const { user } = useCurrentUserContext();

  const canEditLimitRequest = hasPermission(
    user,
    RolePermission.CanEditLimitRequest
  );

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "cuAssignedDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "cuAssignedDate",
          }}
        >
          CU Assigned Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitName",
          }}
        >
          Limit Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 300,
    },
    {
      accessor: "createdAt",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "createdAt",
          }}
        >
          Limit Request Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "companyName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "name",
          }}
        >
          Company Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 300,
    },
    {
      accessor: "salesName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "salesName",
          }}
        >
          Sales Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "salesTeamName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "salesTeamName",
          }}
        >
          Sales Team
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "requestStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "requestStatus",
          }}
        >
          Request Status
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "limitRequestAmount",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitRequestAmount",
          }}
        >
          Limit Request
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "tenorRequest",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "tenorRequest",
          }}
        >
          Tenor Request
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitType",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitRequestType",
          }}
        >
          Limit Type
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "topProductCategory",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "topProductCategory",
          }}
        >
          TOP Product Category
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "cuAssignedId",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "cuAssignedId",
          }}
        >
          CU Assigned
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 250,
    },
  ];

  const handleOnClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    //  stop event propagation (won't trigger parent's onClick)
    event.stopPropagation();
  }, []);

  const renderTopUpCategory = useCallback(
    (item: LimitRequest) => {
      if (item.limitRequestType === LimitRequestType.NewProjectLimit) {
        return toHumanReadable(LimitTopProductCategory.ProjectLimit);
      }
      if (item.limitRequestType === LimitRequestType.TopUpLimit) {
        return toHumanReadable(LimitTopProductCategory.TopUpLimit);
      }
      return (
        <Select
          data={topProductCategoryOptions}
          value={item.topProductCategory}
          withinPortal
          zIndex={100}
          size="s"
          disabled={!canEditLimitRequest}
          onChange={(value: LimitTopProductCategory) =>
            onEdit(item.id, { topProductCategory: value })
          }
          onClick={(event) => handleOnClick(event)}
        />
      );
    },
    [canEditLimitRequest, handleOnClick, onEdit]
  );

  const rows = useMemo(() => {
    return data?.map((item) => {
      return {
        id: item.id,
        cuAssignedDate: toLocalDate(item.cuAssignedDate),
        limitName: item.limitName,
        createdAt: toLocalDate(item.createdAt),
        companyName: item.company.name,
        salesName: item.company.salesName,
        salesTeamName: item.company.salesTeamName,
        limitRequestAmount: toCurrencyPrice(item.limitRequestAmount),
        tenorRequest: toLocalNumber(item.tenorRequest),
        limitType:
          item?.limitRequestType && toHumanReadable(item.limitRequestType),
        topProductCategory: renderTopUpCategory(item),
        cuAssignedId: item?.cuAssigned && getUserDisplayName(item.cuAssigned),
        requestStatus: toHumanReadableLimitRequestStatus(item.requestStatus),
      };
    });
  }, [data, renderTopUpCategory]);

  return [columns, rows];
};
